import { MarketingFlagKey } from "@redotech/feature-flags-keys-marketing/keys";
import { LoadState, mapLoadState } from "@redotech/react-util/load";
import { AttributionScope } from "@redotech/redo-attribution-common/attribution-scope";
import {
  AttributedMetric,
  Attribution,
  AttributionRow,
  attributionByScope,
} from "@redotech/redo-marketing-common/attribution";
import { useRpcQuery } from "@redotech/redo-web/rpc-query/use-rpc-query";
import { useMemo } from "react";
import { flagGatedLoad } from "../attribution/attribution-data";
import { RedoMarketingRpcClientContext } from "../marketing-rpc-client-provider";
import { useTeamFeatureFlag } from "../use-team-feature-flag";

const PLACEHOLDER_INSTANT = Temporal.Instant.fromEpochMilliseconds(0);
const BEGINNING_OF_TIME = Temporal.Instant.fromEpochMilliseconds(0);
const NO_ROWS: AttributionRow[] = [];
const NO_SCOPES: AttributionScope[] = [];
const ZERO_METRIC: AttributedMetric = { revenue: 0, orders: 0 };

export interface AttributionWindow {
  from: Temporal.Instant;
  to: Temporal.Instant;
}

/**
 * ClickHouse-attributed revenue + orders for one grouping (campaign, automation,
 * ...), fetched as its own query so a slow/down ClickHouse never blocks the list.
 */
export function useAttribution({
  groupBy,
  window,
  filter,
}: {
  groupBy: AttributionScope;
  window: AttributionWindow | undefined;
  /** Memoize this: a fresh object each render refetches. */
  filter?: Partial<Record<AttributionScope, string[]>>;
}): LoadState<Attribution> | undefined {
  const scopes = useMemo(() => [groupBy], [groupBy]);
  const rows = useAttributionRows({ groupBy: scopes, window, filter });
  return useMemo(() => {
    if (!rows) {
      return undefined;
    }
    return mapLoadState(rows, (value) => {
      if (!value) {
        return undefined;
      }
      return attributionByScope(value, groupBy);
    });
  }, [rows, groupBy]);
}

/** Since the beginning, for a surface that shows lifetime totals. */
export function useLifetimeAttributionWindow(): AttributionWindow {
  return useMemo(
    () => ({ from: BEGINNING_OF_TIME, to: Temporal.Now.instant() }),
    [],
  );
}

/** Everything the team's email and SMS drove in the window, ungrouped. */
export function useAttributionTotal({
  window,
}: {
  window: AttributionWindow | undefined;
}): LoadState<AttributedMetric> | undefined {
  const rows = useAttributionRows({ groupBy: NO_SCOPES, window });
  // Grouping by nothing rolls the team into one row, so there is nothing to sum.
  return useMemo(() => {
    if (!rows) {
      return undefined;
    }
    return mapLoadState(rows, (value) => {
      if (!value) {
        return undefined;
      }
      return value[0] ?? ZERO_METRIC;
    });
  }, [rows]);
}

function useAttributionRows({
  groupBy,
  window,
  filter,
}: {
  groupBy: AttributionScope[];
  window: AttributionWindow | undefined;
  filter?: Partial<Record<AttributionScope, string[]>>;
}): LoadState<AttributionRow[]> | undefined {
  const { enabled: flagEnabled, pending: flagPending } = useTeamFeatureFlag(
    MarketingFlagKey.MARKETING_ATTRIBUTION_METRICS_CLICKHOUSE,
    { forceEnabledForRedoUsers: false },
  );
  const windowValid =
    window != null && Temporal.Instant.compare(window.from, window.to) < 0;
  const ready = flagEnabled && windowValid;

  const {
    data,
    isLoading: metricsLoading,
    error: metricsError,
  } = useRpcQuery(
    RedoMarketingRpcClientContext,
    "getAttribution",
    {
      groupBy,
      filter,
      from: window?.from ?? PLACEHOLDER_INSTANT,
      to: window?.to ?? PLACEHOLDER_INSTANT,
    },
    { enabled: ready },
  );

  return flagGatedLoad({
    flagEnabled,
    pending: flagPending || metricsLoading,
    error: metricsError,
    data,
    empty: NO_ROWS,
  });
}
